import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { Purchase } from '@Types/purchase';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { getMemberProfile } from '@Utils/fish';
import { useGetPurchases } from '@Hooks/usePurchases';
import { Receipt } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@Utils/utils';

const ticketColumnConfigs: ColumnConfig<Purchase>[] = [
  {
    id: 'fName',
    headerLabel: 'First Name',
    accessor: (row) =>
      getMemberProfile(row.fish)?.fName ??
      row.fish?.fishProfiles?.[0]?.fName ??
      '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'lName',
    headerLabel: 'Last Name',
    accessor: (row) =>
      getMemberProfile(row.fish)?.lName ??
      row.fish?.fishProfiles?.[0]?.lName ??
      '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'eventName',
    headerLabel: 'Event Name',
    accessor: (row) => row.ticketedEventTickets?.[0]?.event?.name ?? '-',
    type: 'text',
    size: 200,
    enableSorting: false,
  },
  {
    id: 'ticketType',
    headerLabel: 'Ticket Type',
    accessor: (row) => {
      const tickets = row.ticketedEventTickets ?? [];
      const uniqueTypes = [
        ...new Set(tickets.map((ticket) => ticket.ticketType?.name)),
      ];
      return uniqueTypes.filter(Boolean).join(', ') || '-';
    },
    type: 'text',
    size: 180,
    enableSorting: false,
  },
  {
    id: 'ticketCount',
    headerLabel: 'Number of Tickets',
    accessor: (row) => row.ticketedEventTickets?.length ?? 0,
    type: 'number',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Purchase Date',
    accessor: (row) =>
      row.createdAt ? DateTime.fromISO(row.createdAt).toMillis() : null,
    type: 'date',
    size: 180,
    cell: (info) => {
      const value = info.getValue() as number | null;
      return value
        ? DateTime.fromMillis(value).toLocaleString(DateTime.DATETIME_MED)
        : '-';
    },
  },
  {
    id: 'status',
    headerLabel: 'Purchase Status',
    accessor: (row) => row.status,
    type: 'text',
    size: 120,
    cell: (info) => {
      const status = info.getValue() as string;
      return <Badge variant="secondary">{status}</Badge>;
    },
  },
  {
    id: 'price',
    headerLabel: 'Total Price',
    accessor: (row) => row.price,
    type: 'currency',
    size: 120,
    cell: (info) => formatCurrency(info.getValue() as number),
    meta: { align: 'right' as const },
  },
];

const TICKET_FIELD_MAPPING: Record<string, string> = {
  fName: 'filter[fish][fishProfiles][some][fName]',
  lName: 'filter[fish][fishProfiles][some][lName]',
};

const TICKET_STATIC_PARAMS: Record<string, string> = {
  'include[fish][include][fishProfiles][filter][deletedAt]': 'null',
};

export default function TicketsList() {
  const navigate = useNavigate();

  const controller = useResourceTableController<Purchase>({
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    columnsConfig: ticketColumnConfigs,
    baseSearchParams: () => ({
      'include':
        'fish.fishProfiles,ticketedEventTickets.ticketType,ticketedEventTickets.event',
      'filter[deletedAt]': null,
      'filter[status]': 'SUCCESS',
      'filter[ticketedEventTickets][some][deletedAt]': 'null',
    }),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: TICKET_FIELD_MAPPING,
        staticParams: TICKET_STATIC_PARAMS,
      }),
    quickFilterFields: [
      { field: 'fName', label: 'First Name' },
      { field: 'lName', label: 'Last Name' },
    ],
    initialPageSize: 10,
  });

  const {
    data: purchases,
    status,
    isError,
    error,
  } = useGetPurchases(controller.searchParams);

  useEffect(() => {
    if (isError) {
      toastError(error.message);
    }
  }, [isError, error]);

  const rows: Purchase[] = Array.isArray(purchases)
    ? purchases
    : (purchases?.data ?? []);

  const meta = Array.isArray(purchases) ? undefined : purchases?.meta;

  return (
    <ResourceTableView<Purchase>
      label="Tickets"
      controller={controller}
      data={rows}
      meta={meta}
      status={status}
      summaryIcon={<Receipt className="h-4 w-4 text-zinc-500" />}
      summaryLabel="Total Ticket Purchases: "
      onRowClick={(row) => navigate(`/events/tickets/purchases/${row.id}`)}
    />
  );
}
